import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useOperator } from '@/lib/operator-context';
import type { Business } from '@/lib/operator-api/types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Building2,
  Sparkles,
  Palette,
  Wrench,
  Check,
  X,
  Calendar,
  MessageSquare,
  CreditCard,
  Mail,
  Lightbulb,
  Scissors,
  ShoppingBag,
  Dumbbell,
  Stethoscope,
  UtensilsCrossed,
  School,
  Car,
  Home,
  Clock,
  CheckCircle2,
  Circle,
  ExternalLink,
  Image,
  User,
  Phone,
  Edit3,
  Briefcase,
  Shield,
  Key,
  AlertCircle,
  Loader2,
} from 'lucide-react';

import TechnicalTab from '@/routes/_auth.operator/-TechnicalTab';

// ============================================================================
// Business type icons
// ============================================================================

const businessTypeIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  salon: Scissors,
  barbershop: Scissors,
  spa: Sparkles,
  fitness: Dumbbell,
  medical: Stethoscope,
  restaurant: UtensilsCrossed,
  retail: ShoppingBag,
  education: School,
  automotive: Car,
  realestate: Home,
  default: Building2,
};

// ============================================================================
// API helpers
// ============================================================================

function getApiHeaders() {
  const token = localStorage.getItem('auth_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

function apiUrl(path: string) {
  return `${import.meta.env.VITE_API_URL || ''}${path}`;
}

// ============================================================================
// Main Settings Page
// ============================================================================

function SettingsPage() {
  const { t } = useTranslation();
  const { operator, isLoading, selectedBusiness } = useOperator();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!operator) return null;

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">{t('settings.title')}</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {t('settings.subtitle')}
          {selectedBusiness && (
            <span className="ml-2 text-primary">- {selectedBusiness.name}</span>
          )}
        </p>
      </div>

      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList className="grid w-full grid-cols-6 bg-[hsl(160,60%,18%)]/5 p-1.5 rounded-xl border border-[hsl(160,60%,18%)]/10">
          <TabsTrigger
            value="profile"
            className="gap-2 data-[state=active]:bg-[hsl(160,60%,18%)] data-[state=active]:text-white data-[state=active]:shadow-md transition-all duration-200 rounded-lg"
          >
            <Briefcase className="h-4 w-4" />
            <span className="hidden sm:inline">{t('settings.tabs.profile')}</span>
          </TabsTrigger>
          <TabsTrigger
            value="hours"
            className="gap-2 data-[state=active]:bg-[hsl(160,60%,18%)] data-[state=active]:text-white data-[state=active]:shadow-md transition-all duration-200 rounded-lg"
          >
            <Clock className="h-4 w-4" />
            <span className="hidden sm:inline">{t('settings.tabs.hours', 'Hours')}</span>
          </TabsTrigger>
          <TabsTrigger
            value="features"
            className="gap-2 data-[state=active]:bg-[hsl(160,60%,18%)] data-[state=active]:text-white data-[state=active]:shadow-md transition-all duration-200 rounded-lg"
          >
            <Sparkles className="h-4 w-4" />
            <span className="hidden sm:inline">{t('settings.tabs.features')}</span>
          </TabsTrigger>
          <TabsTrigger
            value="branding"
            className="gap-2 data-[state=active]:bg-[hsl(160,60%,18%)] data-[state=active]:text-white data-[state=active]:shadow-md transition-all duration-200 rounded-lg"
          >
            <Palette className="h-4 w-4" />
            <span className="hidden sm:inline">{t('settings.tabs.branding')}</span>
          </TabsTrigger>
          <TabsTrigger
            value="connections"
            className="gap-2 data-[state=active]:bg-[hsl(160,60%,18%)] data-[state=active]:text-white data-[state=active]:shadow-md transition-all duration-200 rounded-lg"
          >
            <Key className="h-4 w-4" />
            <span className="hidden sm:inline">{t('settings.tabs.connections', 'Connections')}</span>
          </TabsTrigger>
          <TabsTrigger
            value="technical"
            className="gap-2 data-[state=active]:bg-[hsl(160,60%,18%)] data-[state=active]:text-white data-[state=active]:shadow-md transition-all duration-200 rounded-lg"
          >
            <Wrench className="h-4 w-4" />
            <span className="hidden sm:inline">{t('settings.tabs.technical')}</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="profile">
          <BusinessProfileTab business={selectedBusiness} />
        </TabsContent>

        <TabsContent value="hours">
          <Card>
            <CardHeader>
              <CardTitle>{t('settings.hours.title', 'Hours & Availability')}</CardTitle>
              <CardDescription>{t('settings.hours.subtitle', 'Set your business operating hours for each day of the week')}</CardDescription>
            </CardHeader>
            <CardContent>
              <HoursTab business={selectedBusiness} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="features">
          <Card>
            <CardHeader>
              <CardTitle>{t('settings.features.title')}</CardTitle>
              <CardDescription>{t('settings.features.subtitle')}</CardDescription>
            </CardHeader>
            <CardContent>
              <FeaturesTab />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="branding">
          <Card>
            <CardHeader>
              <CardTitle>{t('settings.branding.title')}</CardTitle>
              <CardDescription>{t('settings.branding.subtitle')}</CardDescription>
            </CardHeader>
            <CardContent>
              <BrandingTab />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="connections">
          <ConnectionsTab />
        </TabsContent>

        <TabsContent value="technical">
          <Card>
            <CardHeader>
              <CardTitle>{t('settings.technical.title')}</CardTitle>
              <CardDescription>{t('settings.technical.subtitle')}</CardDescription>
            </CardHeader>
            <CardContent>
              <TechnicalTab />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ============================================================================
// Business Profile Tab — streamlined, no services/staff/hours
// ============================================================================

function BusinessProfileTab({ business }: { business: Business | null }) {
  const { t } = useTranslation();
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    ownerName: '',
    email: '',
    phone: '',
    category: '',
  });
  const [isSaving, setIsSaving] = useState(false);

  const handleOpenEdit = () => {
    if (business) {
      setEditForm({
        name: business.name || '',
        ownerName: business.ownerName || '',
        email: business.email || '',
        phone: business.phone || '',
        category: business.category || '',
      });
    }
    setIsEditDialogOpen(true);
  };

  const handleSave = async () => {
    if (!business) return;
    setIsSaving(true);
    try {
      const res = await fetch(apiUrl(`/api/operator/businesses/${business.id}`), {
        method: 'PATCH',
        credentials: 'include',
        headers: getApiHeaders(),
        body: JSON.stringify(editForm),
      });
      if (!res.ok) throw new Error(`Save failed: ${res.status}`);
      setIsEditDialogOpen(false);
      window.location.reload();
    } catch (error) {
      console.error('Failed to save business profile:', error);
    } finally {
      setIsSaving(false);
    }
  };

  if (!business) {
    return (
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="py-12 text-center">
          <Building2 className="h-12 w-12 mx-auto mb-4 text-amber-500" />
          <h3 className="text-lg font-semibold text-amber-800 mb-2">{t('settings.profile.noBusinessSelected')}</h3>
          <p className="text-sm text-amber-600">{t('settings.profile.selectBusinessPrompt')}</p>
        </CardContent>
      </Card>
    );
  }

  const statusColors: Record<string, string> = {
    ACTIVE: 'bg-emerald-500',
    PENDING_SETUP: 'bg-amber-500',
    SUSPENDED: 'bg-red-500',
    INACTIVE: 'bg-gray-400',
  };

  const BusinessIcon = businessTypeIcons[business.category?.toLowerCase()] || Building2;

  return (
    <div className="space-y-6">
      {/* Business Header Card */}
      <Card className="overflow-hidden">
        <div className="bg-gradient-to-r from-[hsl(160,60%,18%)] to-[hsl(160,60%,25%)] p-6 text-white">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center backdrop-blur-sm">
                <BusinessIcon className="h-8 w-8" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">{business.name}</h2>
                <div className="flex items-center gap-3 mt-1">
                  <Badge className="bg-white/20 hover:bg-white/30 text-white border-0">
                    {business.category?.replace(/_/g, ' ') || 'Business'}
                  </Badge>
                  <div className="flex items-center gap-1.5">
                    <span className={`w-2 h-2 rounded-full ${statusColors[business.status] || 'bg-gray-400'}`} />
                    <span className="text-sm text-white/80">{business.status?.replace(/_/g, ' ')}</span>
                  </div>
                </div>
              </div>
            </div>
            <Button variant="secondary" size="sm" className="gap-2" onClick={handleOpenEdit}>
              <Edit3 className="h-4 w-4" />
              {t('settings.profile.editProfile')}
            </Button>
          </div>
        </div>

        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Contact Info */}
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">{t('settings.profile.contact')}</h4>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="h-4 w-4 text-[hsl(160,84%,39%)]" />
                  <span>{business.email || t('settings.profile.notSet')}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="h-4 w-4 text-[hsl(160,84%,39%)]" />
                  <span>{business.phone || t('settings.profile.notSet')}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <User className="h-4 w-4 text-[hsl(160,84%,39%)]" />
                  <span>{business.ownerName || t('settings.profile.notSet')}</span>
                </div>
              </div>
            </div>

            {/* Activity Stats */}
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">{t('settings.profile.activity')}</h4>
              <div className="grid grid-cols-1 gap-2">
                <div className="flex items-center justify-between p-2 rounded-lg bg-[hsl(160,84%,39%)]/5">
                  <span className="text-sm text-muted-foreground">{t('settings.profile.conversations')}</span>
                  <span className="font-semibold text-[hsl(160,60%,18%)]">{business.conversationCount}</span>
                </div>
                <div className="flex items-center justify-between p-2 rounded-lg bg-[hsl(160,84%,39%)]/5">
                  <span className="text-sm text-muted-foreground">{t('settings.profile.customers')}</span>
                  <span className="font-semibold text-[hsl(160,60%,18%)]">{business.customerCount}</span>
                </div>
                <div className="flex items-center justify-between p-2 rounded-lg bg-[hsl(160,84%,39%)]/5">
                  <span className="text-sm text-muted-foreground">{t('settings.profile.bookings')}</span>
                  <span className="font-semibold text-[hsl(160,60%,18%)]">{business.bookingCount}</span>
                </div>
              </div>
            </div>

            {/* Created / Timezone */}
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">{t('settings.profile.settingsLabel')}</h4>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4 text-[hsl(160,84%,39%)]" />
                  <span>{t('settings.profile.created', { date: business.createdAt ? new Date(business.createdAt).toLocaleDateString() : t('settings.profile.unknown') })}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="h-4 w-4 text-[hsl(160,84%,39%)]" />
                  <span>America/Sao_Paulo</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Edit Profile Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit3 className="h-5 w-5 text-[hsl(160,84%,39%)]" />
              {t('settings.editDialog.title')}
            </DialogTitle>
            <DialogDescription>{t('settings.editDialog.description')}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="business-name">{t('settings.editDialog.businessName')}</Label>
              <Input id="business-name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="owner-name">{t('settings.editDialog.ownerName')}</Label>
              <Input id="owner-name" value={editForm.ownerName} onChange={(e) => setEditForm({ ...editForm, ownerName: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="business-email">{t('settings.editDialog.email')}</Label>
                <Input id="business-email" type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="business-phone">{t('settings.editDialog.phone')}</Label>
                <Input id="business-phone" type="tel" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} />
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="business-category">{t('settings.editDialog.category')}</Label>
              <select
                id="business-category"
                value={editForm.category}
                onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                className="flex h-10 w-full rounded-md border border-[hsl(160,60%,18%)]/20 bg-background px-3 py-2 text-sm"
              >
                <option value="">{t('settings.editDialog.selectCategory')}</option>
                <option value="salon">{t('settings.categories.salon')}</option>
                <option value="barbershop">{t('settings.categories.barbershop')}</option>
                <option value="spa">{t('settings.categories.spa')}</option>
                <option value="fitness">{t('settings.categories.fitness')}</option>
                <option value="medical">{t('settings.categories.medical')}</option>
                <option value="restaurant">{t('settings.categories.restaurant')}</option>
                <option value="retail">{t('settings.categories.retail')}</option>
                <option value="education">{t('settings.categories.education')}</option>
                <option value="automotive">{t('settings.categories.automotive')}</option>
                <option value="realestate">{t('settings.categories.realestate')}</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleSave} disabled={isSaving} className="bg-[hsl(160,60%,18%)] hover:bg-[hsl(160,60%,22%)] text-white">
              {isSaving ? t('settings.editDialog.saving') : t('settings.editDialog.saveChanges')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// Hours & Availability Tab — promoted from Profile sub-section
// ============================================================================

function HoursTab({ business }: { business: Business | null }) {
  const { t } = useTranslation();
  const [hours, setHours] = useState<Record<string, { open: string; close: string } | null>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<Record<string, { open: string; close: string } | null>>({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!business?.id) return;
    setIsLoading(true);
    fetch(apiUrl(`/api/operator/businesses/${business.id}/hours`), {
      credentials: 'include',
      headers: getApiHeaders(),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success && data.data) setHours(data.data);
      })
      .catch((err) => console.error('Failed to fetch hours:', err))
      .finally(() => setIsLoading(false));
  }, [business?.id]);

  const startEdit = () => {
    setEditForm(hours);
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!business) return;
    setIsSaving(true);
    try {
      const res = await fetch(apiUrl(`/api/operator/businesses/${business.id}/hours`), {
        method: 'PUT',
        credentials: 'include',
        headers: getApiHeaders(),
        body: JSON.stringify(editForm),
      });
      if (!res.ok) throw new Error(`Save failed: ${res.status}`);
      setHours(editForm);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save hours:', error);
    } finally {
      setIsSaving(false);
    }
  };

  if (!business) {
    return <p className="text-sm text-muted-foreground text-center py-8">{t('settings.profile.noBusinessSelected')}</p>;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[hsl(160,60%,18%)]"></div>
      </div>
    );
  }

  if (isEditing) {
    return (
      <div className="space-y-4">
        {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => (
          <div key={day} className="grid grid-cols-[120px_1fr_1fr_auto] gap-3 items-center">
            <Label className="capitalize font-medium">{day}</Label>
            <Input
              type="time"
              value={editForm[day]?.open || ''}
              onChange={(e) =>
                setEditForm({
                  ...editForm,
                  [day]: e.target.value ? { open: e.target.value, close: editForm[day]?.close || '17:00' } : null,
                })
              }
              placeholder="Open"
            />
            <Input
              type="time"
              value={editForm[day]?.close || ''}
              onChange={(e) =>
                setEditForm({
                  ...editForm,
                  [day]: e.target.value ? { open: editForm[day]?.open || '09:00', close: e.target.value } : null,
                })
              }
              disabled={!editForm[day]}
            />
            <Button variant="ghost" size="sm" onClick={() => setEditForm({ ...editForm, [day]: null })} className="text-red-600 hover:text-red-700">
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}
        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={() => setIsEditing(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleSave} disabled={isSaving} className="bg-[hsl(160,60%,18%)] hover:bg-[hsl(160,60%,22%)] text-white">
            {isSaving ? t('settings.editDialog.saving') : t('settings.editDialog.saveChanges')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button size="sm" variant="outline" className="gap-1.5" onClick={startEdit}>
          <Edit3 className="h-4 w-4" />
          {t('settings.profile.editHours')}
        </Button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        {Object.entries(hours).map(([day, dayHours]) => (
          <div
            key={day}
            className={`p-4 rounded-xl text-center transition-all ${
              dayHours ? 'bg-[hsl(160,84%,39%)]/5 border-2 border-[hsl(160,84%,39%)]/20' : 'bg-muted/30 border-2 border-transparent'
            }`}
          >
            <p className="font-semibold capitalize text-foreground mb-1">{day.slice(0, 3)}</p>
            {dayHours ? (
              <div className="text-sm">
                <p className="text-[hsl(160,60%,18%)] font-medium">{dayHours.open}</p>
                <p className="text-muted-foreground text-xs">{t('settings.profile.to')}</p>
                <p className="text-[hsl(160,60%,18%)] font-medium">{dayHours.close}</p>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">{t('settings.profile.closed')}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Features Tab — unchanged from original
// ============================================================================

const FEATURE_KEY_MAP: Record<string, string> = {
  'Online Booking': 'onlineBooking',
  'SMS Reminders': 'smsReminders',
  'WhatsApp Integration': 'whatsappIntegration',
  'Payment Processing': 'paymentProcessing',
  Waitlist: 'waitlist',
  'Gift Cards': 'giftCards',
  'Loyalty Program': 'loyaltyProgram',
  'Staff Scheduling': 'staffScheduling',
};
const FEATURE_KEY_REVERSE: Record<string, string> = Object.fromEntries(Object.entries(FEATURE_KEY_MAP).map(([k, v]) => [v, k]));

const featureIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  'Online Booking': Calendar,
  'SMS Reminders': MessageSquare,
  'WhatsApp Integration': MessageSquare,
  'Payment Processing': CreditCard,
  Waitlist: Clock,
  'Gift Cards': CreditCard,
  'Loyalty Program': Sparkles,
  'Staff Scheduling': Calendar,
};

function FeaturesTab() {
  const { t } = useTranslation();
  const { selectedBusiness } = useOperator();
  const [featureStates, setFeatureStates] = useState<Record<string, boolean>>({
    'Online Booking': true,
    'SMS Reminders': true,
    'WhatsApp Integration': true,
    'Payment Processing': false,
    Waitlist: false,
    'Gift Cards': false,
    'Loyalty Program': false,
    'Staff Scheduling': true,
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!selectedBusiness?.id) return;
    setIsLoading(true);
    fetch(apiUrl(`/api/operator/settings/features?businessId=${selectedBusiness.id}`), {
      credentials: 'include',
      headers: getApiHeaders(),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success && data.data) {
          const mapped: Record<string, boolean> = {};
          for (const [apiKey, val] of Object.entries(data.data as Record<string, boolean>)) {
            const displayKey = FEATURE_KEY_REVERSE[apiKey];
            if (displayKey) mapped[displayKey] = val;
          }
          setFeatureStates((prev) => ({ ...prev, ...mapped }));
        }
      })
      .catch((err) => console.error('Failed to load features:', err))
      .finally(() => setIsLoading(false));
  }, [selectedBusiness?.id]);

  const features = [
    { name: t('settings.features.onlineBooking'), description: t('settings.features.onlineBookingDesc'), category: t('settings.features.categories.booking'), key: 'Online Booking' },
    { name: t('settings.features.smsReminders'), description: t('settings.features.smsRemindersDesc'), category: t('settings.features.categories.communication'), key: 'SMS Reminders' },
    { name: t('settings.features.whatsappIntegration'), description: t('settings.features.whatsappIntegrationDesc'), category: t('settings.features.categories.communication'), key: 'WhatsApp Integration' },
    { name: t('settings.features.paymentProcessing'), description: t('settings.features.paymentProcessingDesc'), category: t('settings.features.categories.payments'), key: 'Payment Processing' },
    { name: t('settings.features.waitlist'), description: t('settings.features.waitlistDesc'), category: t('settings.features.categories.booking'), key: 'Waitlist' },
    { name: t('settings.features.giftCards'), description: t('settings.features.giftCardsDesc'), category: t('settings.features.categories.payments'), key: 'Gift Cards' },
    { name: t('settings.features.loyaltyProgram'), description: t('settings.features.loyaltyProgramDesc'), category: t('settings.features.categories.marketing'), key: 'Loyalty Program' },
    { name: t('settings.features.staffScheduling'), description: t('settings.features.staffSchedulingDesc'), category: t('settings.features.categories.management'), key: 'Staff Scheduling' },
  ];

  const toggleFeature = async (key: string) => {
    const newValue = !featureStates[key];
    setFeatureStates((prev) => ({ ...prev, [key]: newValue }));
    if (!selectedBusiness?.id) return;
    const apiKey = FEATURE_KEY_MAP[key];
    if (!apiKey) return;
    try {
      await fetch(apiUrl(`/api/operator/settings/features?businessId=${selectedBusiness.id}`), {
        method: 'PATCH',
        credentials: 'include',
        headers: getApiHeaders(),
        body: JSON.stringify({ [apiKey]: newValue }),
      });
    } catch {
      setFeatureStates((prev) => ({ ...prev, [key]: !newValue }));
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[hsl(160,60%,18%)]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {features.map((feature, index) => {
        const enabled = featureStates[feature.key];
        const IconComponent = featureIcons[feature.key] || Sparkles;
        return (
          <div
            key={index}
            className={`group flex items-center justify-between p-4 rounded-xl border-2 transition-all duration-300 cursor-pointer hover:shadow-md ${
              enabled ? 'bg-[hsl(160,84%,39%)]/5 border-[hsl(160,84%,39%)]/20' : 'bg-muted/30 border-border hover:border-[hsl(160,60%,18%)]/20'
            }`}
            onClick={() => toggleFeature(feature.key)}
          >
            <div className="flex items-center gap-4">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all ${enabled ? 'bg-[hsl(160,84%,39%)] text-white' : 'bg-muted text-muted-foreground'}`}>
                <IconComponent className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-medium text-foreground flex items-center gap-2">
                  {feature.name}
                  <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${enabled ? 'border-[hsl(160,84%,39%)]/30 text-[hsl(160,60%,18%)]' : 'border-border'}`}>
                    {feature.category}
                  </Badge>
                </h3>
                <p className="text-sm text-muted-foreground">{feature.description}</p>
              </div>
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); toggleFeature(feature.key); }}
              className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-all ${enabled ? 'bg-[hsl(160,84%,39%)]' : 'bg-muted-foreground/20'}`}
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-lg transition-all ${enabled ? 'translate-x-6' : 'translate-x-1'}`}>
                {enabled && <Check className="h-3 w-3 text-[hsl(160,84%,39%)] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />}
              </span>
            </button>
          </div>
        );
      })}
      <div className="flex items-center justify-center gap-3 mt-6 p-4 rounded-xl bg-amber-500/5 border border-amber-500/20 text-sm text-amber-700">
        <Lightbulb className="h-5 w-5 shrink-0" />
        <span>{t('settings.features.notice')}</span>
      </div>
    </div>
  );
}

// ============================================================================
// Branding Tab — unchanged from original
// ============================================================================

function BrandingTab() {
  const { t } = useTranslation();
  const { selectedBusiness } = useOperator();
  const [primaryColor, setPrimaryColor] = useState('#0D4F3C');
  const [secondaryColor, setSecondaryColor] = useState('#10B981');
  const [logoUrl, setLogoUrl] = useState('');
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (!selectedBusiness?.id) return;
    setIsLoading(true);
    fetch(apiUrl(`/api/operator/settings/branding?businessId=${selectedBusiness.id}`), {
      credentials: 'include',
      headers: getApiHeaders(),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success && data.data) {
          setPrimaryColor(data.data.primaryColor || '#0D4F3C');
          setSecondaryColor(data.data.accentColor || '#10B981');
          setLogoUrl(data.data.logoUrl || '');
          setWelcomeMessage(data.data.welcomeMessage || '');
        }
      })
      .catch((err) => console.error('Failed to load branding:', err))
      .finally(() => setIsLoading(false));
  }, [selectedBusiness?.id]);

  const handleSave = async () => {
    if (!selectedBusiness?.id) return;
    setIsSaving(true);
    try {
      const res = await fetch(apiUrl(`/api/operator/settings/branding?businessId=${selectedBusiness.id}`), {
        method: 'PATCH',
        credentials: 'include',
        headers: getApiHeaders(),
        body: JSON.stringify({ logoUrl, primaryColor, accentColor: secondaryColor, welcomeMessage }),
      });
      if (!res.ok) throw new Error(`Save failed: ${res.status}`);
      setHasChanges(false);
    } catch (error) {
      console.error('Failed to save branding:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const presetColors = [
    { name: 'Forest Green', primary: '#0D4F3C', secondary: '#10B981' },
    { name: 'Ocean Blue', primary: '#1E3A5F', secondary: '#3B82F6' },
    { name: 'Sunset Orange', primary: '#7C2D12', secondary: '#F97316' },
    { name: 'Royal Purple', primary: '#4C1D95', secondary: '#8B5CF6' },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[hsl(160,60%,18%)]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Logo */}
      <div className="p-6 rounded-xl border-2 border-dashed border-[hsl(160,60%,18%)]/20">
        <div className="flex flex-col sm:flex-row items-center gap-6">
          <div className="w-24 h-24 bg-gradient-to-br from-[hsl(160,60%,18%)]/10 to-[hsl(160,84%,39%)]/10 rounded-2xl flex items-center justify-center overflow-hidden">
            {logoUrl ? <img src={logoUrl} alt="Logo" className="w-full h-full object-cover" /> : <Image className="h-10 w-10 text-[hsl(160,60%,18%)]" />}
          </div>
          <div className="flex-1 text-center sm:text-left">
            <h3 className="font-semibold text-foreground mb-1">{t('settings.branding.businessLogo')}</h3>
            <p className="text-sm text-muted-foreground mb-3">{t('settings.branding.logoDescription')}</p>
            <Input
              type="text"
              value={logoUrl}
              onChange={(e) => { setLogoUrl(e.target.value); setHasChanges(true); }}
              placeholder={t('settings.branding.logoUrlPlaceholder') || 'https://example.com/logo.png'}
            />
          </div>
        </div>
      </div>

      {/* Colors */}
      <div className="space-y-6">
        <div>
          <h3 className="font-semibold text-foreground mb-1 flex items-center gap-2">
            <Palette className="h-4 w-4 text-[hsl(160,84%,39%)]" />
            {t('settings.branding.brandColors')}
          </h3>
          <p className="text-sm text-muted-foreground">{t('settings.branding.brandColorsDescription')}</p>
        </div>
        <div className="flex flex-wrap gap-3">
          {presetColors.map((preset) => (
            <button
              key={preset.name}
              onClick={() => { setPrimaryColor(preset.primary); setSecondaryColor(preset.secondary); setHasChanges(true); }}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all ${primaryColor === preset.primary ? 'border-[hsl(160,84%,39%)] bg-[hsl(160,84%,39%)]/5' : 'border-border'}`}
            >
              <div className="flex -space-x-1">
                <div className="w-5 h-5 rounded-full border-2 border-white shadow-sm" style={{ backgroundColor: preset.primary }} />
                <div className="w-5 h-5 rounded-full border-2 border-white shadow-sm" style={{ backgroundColor: preset.secondary }} />
              </div>
              <span className="text-sm font-medium">{preset.name}</span>
              {primaryColor === preset.primary && <Check className="h-3.5 w-3.5 text-[hsl(160,84%,39%)]" />}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="p-4 rounded-xl bg-muted/30 space-y-3">
            <label className="text-sm font-medium">{t('settings.branding.primaryColor')}</label>
            <div className="flex items-center gap-3">
              <input type="color" value={primaryColor} onChange={(e) => { setPrimaryColor(e.target.value); setHasChanges(true); }} className="sr-only" id="primary-color" />
              <label htmlFor="primary-color" className="block w-14 h-14 rounded-xl cursor-pointer shadow-lg border-4 border-white" style={{ backgroundColor: primaryColor }} />
              <Input type="text" value={primaryColor} onChange={(e) => { setPrimaryColor(e.target.value); setHasChanges(true); }} className="flex-1 font-mono text-sm uppercase" />
            </div>
          </div>
          <div className="p-4 rounded-xl bg-muted/30 space-y-3">
            <label className="text-sm font-medium">{t('settings.branding.accentColor')}</label>
            <div className="flex items-center gap-3">
              <input type="color" value={secondaryColor} onChange={(e) => { setSecondaryColor(e.target.value); setHasChanges(true); }} className="sr-only" id="secondary-color" />
              <label htmlFor="secondary-color" className="block w-14 h-14 rounded-xl cursor-pointer shadow-lg border-4 border-white" style={{ backgroundColor: secondaryColor }} />
              <Input type="text" value={secondaryColor} onChange={(e) => { setSecondaryColor(e.target.value); setHasChanges(true); }} className="flex-1 font-mono text-sm uppercase" />
            </div>
          </div>
        </div>
        <div className="p-4 rounded-xl border">
          <label className="text-xs text-muted-foreground mb-3 block">{t('settings.branding.preview')}</label>
          <div className="flex items-center gap-4">
            <Button style={{ backgroundColor: primaryColor }}>{t('settings.branding.primaryButton')}</Button>
            <Button variant="outline" style={{ borderColor: secondaryColor, color: secondaryColor }}>{t('settings.branding.secondaryButton')}</Button>
            <Badge style={{ backgroundColor: secondaryColor + '20', color: primaryColor }}>{t('settings.branding.sampleBadge')}</Badge>
          </div>
        </div>
      </div>

      {/* Welcome Message */}
      <div className="space-y-3">
        <div>
          <h3 className="font-semibold text-foreground mb-1 flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-[hsl(160,84%,39%)]" />
            {t('settings.branding.welcomeMessage')}
          </h3>
          <p className="text-sm text-muted-foreground">{t('settings.branding.welcomeMessageDescription')}</p>
        </div>
        <textarea
          className="flex min-h-[100px] w-full rounded-xl border-2 border-[hsl(160,60%,18%)]/10 bg-transparent px-4 py-3 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(160,84%,39%)] resize-none"
          placeholder={t('settings.branding.welcomePlaceholder')}
          value={welcomeMessage}
          onChange={(e) => { setWelcomeMessage(e.target.value); setHasChanges(true); }}
        />
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 p-4 rounded-xl bg-amber-500/5 border border-amber-500/20 text-sm text-amber-700">
          <Lightbulb className="h-5 w-5 shrink-0" />
          <span>{t('settings.branding.notice')}</span>
        </div>
        <Button onClick={handleSave} disabled={isSaving || !hasChanges} className="bg-[hsl(160,60%,18%)] hover:bg-[hsl(160,60%,22%)] text-white">
          {isSaving ? t('settings.editDialog.saving') : t('settings.branding.saveSettings')}
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// Connections Tab — API Keys + Integration Status
// ============================================================================

interface CredentialEntry {
  id: string;
  provider: string;
  status: string;
  lastVerifiedAt: string | null;
  credentials: Record<string, string>;
  scope: 'platform' | 'business';
}

const PROVIDER_CONFIG: Record<
  string,
  { name: string; icon: React.ComponentType<{ className?: string }>; color: string; fields: { key: string; label: string; placeholder: string }[]; scope: 'platform' | 'business' }
> = {
  workos: {
    name: 'WorkOS',
    icon: Shield,
    color: '#6363f1',
    scope: 'platform',
    fields: [
      { key: 'clientId', label: 'Client ID', placeholder: 'client_...' },
      { key: 'apiKey', label: 'API Key', placeholder: 'sk_...' },
    ],
  },
  openai: {
    name: 'OpenAI',
    icon: Sparkles,
    color: '#10a37f',
    scope: 'platform',
    fields: [{ key: 'apiKey', label: 'API Key', placeholder: 'sk-...' }],
  },
  whatsapp: {
    name: 'WhatsApp Business',
    icon: MessageSquare,
    color: '#25d366',
    scope: 'business',
    fields: [
      { key: 'phoneNumberId', label: 'Phone Number ID', placeholder: '1234567890' },
      { key: 'accessToken', label: 'Access Token', placeholder: 'EAAx...' },
    ],
  },
  meta: {
    name: 'Meta (Facebook)',
    icon: ExternalLink,
    color: '#1877f2',
    scope: 'business',
    fields: [
      { key: 'appId', label: 'App ID', placeholder: '123456789' },
      { key: 'appSecret', label: 'App Secret', placeholder: 'abc123...' },
    ],
  },
  stripe: {
    name: 'Stripe',
    icon: CreditCard,
    color: '#635bff',
    scope: 'business',
    fields: [
      { key: 'publishableKey', label: 'Publishable Key', placeholder: 'pk_...' },
      { key: 'secretKey', label: 'Secret Key', placeholder: 'sk_...' },
    ],
  },
  sendgrid: {
    name: 'SendGrid',
    icon: Mail,
    color: '#1a82e2',
    scope: 'business',
    fields: [
      { key: 'apiKey', label: 'API Key', placeholder: 'SG...' },
      { key: 'fromEmail', label: 'From Email', placeholder: 'noreply@business.com' },
    ],
  },
  google_calendar: {
    name: 'Google Calendar',
    icon: Calendar,
    color: '#4285f4',
    scope: 'business',
    fields: [
      { key: 'clientId', label: 'Client ID', placeholder: '...apps.googleusercontent.com' },
      { key: 'clientSecret', label: 'Client Secret', placeholder: 'GOCSPX-...' },
    ],
  },
};

function ConnectionsTab() {
  const { t } = useTranslation();
  const { selectedBusiness, operator } = useOperator();
  const [credentials, setCredentials] = useState<CredentialEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [savingProvider, setSavingProvider] = useState<string | null>(null);

  const isAdmin = operator?.role === 'ADMIN';

  const fetchCredentials = useCallback(async () => {
    if (!selectedBusiness?.id) return;
    setIsLoading(true);
    try {
      const res = await fetch(apiUrl(`/api/operator/settings/credentials?businessId=${selectedBusiness.id}`), {
        credentials: 'include',
        headers: getApiHeaders(),
      });
      const data = await res.json();
      if (data.success) setCredentials(data.data || []);
    } catch (err) {
      console.error('Failed to load credentials:', err);
    } finally {
      setIsLoading(false);
    }
  }, [selectedBusiness?.id]);

  useEffect(() => {
    fetchCredentials();
  }, [fetchCredentials]);

  const handleSave = async (provider: string, values: Record<string, string>) => {
    if (!selectedBusiness?.id) return;
    setSavingProvider(provider);
    try {
      const config = PROVIDER_CONFIG[provider];
      const isPlatform = config?.scope === 'platform';
      const url = apiUrl(`/api/operator/settings/credentials/${provider}${isPlatform ? '' : `?businessId=${selectedBusiness.id}`}`);
      const res = await fetch(url, {
        method: 'PUT',
        credentials: 'include',
        headers: getApiHeaders(),
        body: JSON.stringify({ credentials: values }),
      });
      if (!res.ok) throw new Error('Save failed');
      await fetchCredentials();
    } catch (err) {
      console.error('Failed to save credentials:', err);
    } finally {
      setSavingProvider(null);
    }
  };

  const handleDelete = async (provider: string) => {
    if (!selectedBusiness?.id) return;
    const config = PROVIDER_CONFIG[provider];
    const isPlatform = config?.scope === 'platform';
    try {
      await fetch(apiUrl(`/api/operator/settings/credentials/${provider}${isPlatform ? '' : `?businessId=${selectedBusiness.id}`}`), {
        method: 'DELETE',
        credentials: 'include',
        headers: getApiHeaders(),
      });
      await fetchCredentials();
    } catch (err) {
      console.error('Failed to delete credentials:', err);
    }
  };

  if (!selectedBusiness) {
    return (
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="py-12 text-center">
          <Key className="h-12 w-12 mx-auto mb-4 text-amber-500" />
          <h3 className="text-lg font-semibold text-amber-800 mb-2">{t('settings.profile.noBusinessSelected')}</h3>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[hsl(160,60%,18%)]"></div>
      </div>
    );
  }

  const credentialsByProvider = new Map(credentials.map((c) => [c.provider, c]));
  const configuredCount = credentials.filter((c) => c.status === 'configured' || c.status === 'verified').length;
  const totalProviders = Object.keys(PROVIDER_CONFIG).length;

  const platformProviders = Object.entries(PROVIDER_CONFIG).filter(([, c]) => c.scope === 'platform');
  const businessProviders = Object.entries(PROVIDER_CONFIG).filter(([, c]) => c.scope === 'business');

  return (
    <div className="space-y-8">
      {/* Stats */}
      <div className="flex items-center gap-4 p-4 rounded-xl bg-[hsl(160,84%,39%)]/5 border border-[hsl(160,84%,39%)]/20">
        <div className="w-12 h-12 rounded-xl bg-[hsl(160,84%,39%)] flex items-center justify-center text-white">
          <Key className="h-6 w-6" />
        </div>
        <div>
          <div className="text-2xl font-bold text-foreground">
            {configuredCount}/{totalProviders}
          </div>
          <div className="text-sm text-muted-foreground">{t('settings.connections.configured', 'integrations configured')}</div>
        </div>
      </div>

      {!isAdmin && (
        <div className="flex items-center gap-3 p-4 rounded-xl bg-amber-500/5 border border-amber-500/20 text-sm text-amber-700">
          <AlertCircle className="h-5 w-5 shrink-0" />
          <span>{t('settings.connections.adminOnly', 'Only admins can manage integration credentials. Contact your admin to configure.')}</span>
        </div>
      )}

      {/* Platform-Level Credentials */}
      <section>
        <h2 className="text-lg font-semibold mb-1 flex items-center gap-2">
          <Shield className="h-5 w-5 text-[hsl(160,84%,39%)]" />
          {t('settings.connections.platformTitle', 'Platform Credentials')}
        </h2>
        <p className="text-sm text-muted-foreground mb-4">{t('settings.connections.platformDesc', 'Global keys shared across all businesses')}</p>
        <div className="space-y-4">
          {platformProviders.map(([provider, config]) => (
            <CredentialCard
              key={provider}
              provider={provider}
              config={config}
              credential={credentialsByProvider.get(provider) || null}
              isAdmin={isAdmin}
              isSaving={savingProvider === provider}
              onSave={(values) => handleSave(provider, values)}
              onDelete={() => handleDelete(provider)}
            />
          ))}
        </div>
      </section>

      {/* Business-Level Credentials */}
      <section>
        <h2 className="text-lg font-semibold mb-1 flex items-center gap-2">
          <Building2 className="h-5 w-5 text-[hsl(160,84%,39%)]" />
          {t('settings.connections.businessTitle', 'Business Credentials')}
        </h2>
        <p className="text-sm text-muted-foreground mb-4">
          {t('settings.connections.businessDesc', 'Keys specific to')} {selectedBusiness.name}
        </p>
        <div className="space-y-4">
          {businessProviders.map(([provider, config]) => (
            <CredentialCard
              key={provider}
              provider={provider}
              config={config}
              credential={credentialsByProvider.get(provider) || null}
              isAdmin={isAdmin}
              isSaving={savingProvider === provider}
              onSave={(values) => handleSave(provider, values)}
              onDelete={() => handleDelete(provider)}
            />
          ))}
        </div>
      </section>
    </div>
  );
}

function CredentialCard({
  provider,
  config,
  credential,
  isAdmin,
  isSaving,
  onSave,
  onDelete,
}: {
  provider: string;
  config: (typeof PROVIDER_CONFIG)[string];
  credential: CredentialEntry | null;
  isAdmin: boolean;
  isSaving: boolean;
  onSave: (values: Record<string, string>) => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState<Record<string, string>>({});
  const IconComponent = config.icon;
  const isConfigured = credential && (credential.status === 'configured' || credential.status === 'verified');

  const startEditing = () => {
    setForm({});
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setForm({});
    setIsEditing(false);
  };

  const handleSubmit = () => {
    onSave(form);
    setIsEditing(false);
    setForm({});
  };

  const allFieldsFilled = config.fields.every((f) => form[f.key]?.trim());

  return (
    <div
      className={`rounded-xl border-2 transition-all duration-200 ${
        isConfigured
          ? 'border-[hsl(160,84%,39%)]/30 bg-[hsl(160,84%,39%)]/[0.02]'
          : 'border-border'
      }`}
    >
      {/* Header row */}
      <div className="flex items-center gap-3 px-5 py-4">
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
          style={{ backgroundColor: `${config.color}12`, color: config.color }}
        >
          <IconComponent className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-foreground text-sm">{config.name}</h3>
        </div>
        {isConfigured ? (
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[hsl(160,84%,39%)]/10 text-[hsl(160,60%,18%)]">
            <CheckCircle2 className="h-3.5 w-3.5" />
            <span className="text-xs font-medium">{t('settings.connections.active', 'Active')}</span>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-muted text-muted-foreground">
            <Circle className="h-3.5 w-3.5" />
            <span className="text-xs font-medium">{t('settings.connections.notConfigured', 'Not set')}</span>
          </div>
        )}
      </div>

      {/* Fields */}
      <div className="px-5 pb-4 space-y-3">
        {config.fields.map((field) => {
          const maskedValue = isConfigured && credential?.credentials?.[field.key];

          return (
            <div key={field.key}>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">
                {field.label}
              </label>
              {isEditing ? (
                <Input
                  type="password"
                  value={form[field.key] || ''}
                  onChange={(e) => setForm({ ...form, [field.key]: e.target.value })}
                  placeholder={field.placeholder}
                  className="h-9 font-mono text-sm"
                  autoComplete="off"
                />
              ) : (
                <div className="h-9 flex items-center px-3 rounded-md bg-muted/50 border border-border/50 font-mono text-sm text-muted-foreground">
                  {maskedValue || (
                    <span className="text-muted-foreground/50 italic text-xs">{field.placeholder}</span>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Action buttons */}
        {isAdmin && (
          <div className="flex items-center gap-2 pt-1">
            {isEditing ? (
              <>
                <Button
                  size="sm"
                  className="h-8 text-xs bg-[hsl(160,60%,18%)] hover:bg-[hsl(160,60%,22%)] text-white"
                  disabled={!allFieldsFilled || isSaving}
                  onClick={handleSubmit}
                >
                  {isSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Check className="h-3.5 w-3.5 mr-1.5" />}
                  {t('common.save', 'Save')}
                </Button>
                <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={cancelEditing}>
                  {t('common.cancel', 'Cancel')}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs gap-1.5"
                  onClick={startEditing}
                >
                  <Edit3 className="h-3.5 w-3.5" />
                  {isConfigured
                    ? t('settings.connections.reconfigure', 'Update Keys')
                    : t('settings.connections.configure', 'Add Keys')}
                </Button>
                {isConfigured && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 text-xs text-red-500 hover:text-red-600 hover:bg-red-50"
                    onClick={onDelete}
                  >
                    <X className="h-3.5 w-3.5 mr-1" />
                    {t('settings.connections.remove', 'Remove')}
                  </Button>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Route Export
// ============================================================================

export const Route = createFileRoute('/_auth/operator/settings')({
  component: SettingsPage,
});
